import { useCallback, useRef } from 'react';
import type { SecurityLogEntry } from '../types/security';

const LOG_WINDOW_MS = 60 * 1000;
const DUPLICATE_WINDOW_MS = 5 * 60 * 1000;
const MAX_LOGS_PER_WINDOW = 20;

export function useSecurityLogSender() {
  const sentTimestampsRef = useRef<number[]>([]);
  const lastSentByKeyRef = useRef<Record<string, number>>({});

  const shouldSend = useCallback((entry: SecurityLogEntry): boolean => {
    const now = Date.now();

    sentTimestampsRef.current = sentTimestampsRef.current.filter(
      (timestamp) => now - timestamp < LOG_WINDOW_MS
    );

    if (sentTimestampsRef.current.length >= MAX_LOGS_PER_WINDOW) {
      return false;
    }

    const reason = String(entry.details?.reason ?? 'none');
    const dedupeKey = `${entry.event}:${reason}`;
    const lastSent = lastSentByKeyRef.current[dedupeKey] ?? 0;

    if (now - lastSent < DUPLICATE_WINDOW_MS) {
      return false;
    }

    sentTimestampsRef.current.push(now);
    lastSentByKeyRef.current[dedupeKey] = now;
    return true;
  }, []);

  const sendSecurityLog = useCallback(async (entry: SecurityLogEntry): Promise<void> => {
    if (process.env.NODE_ENV !== 'production') {
      return;
    }

    if (!shouldSend(entry)) {
      return;
    }

    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    if (!apiUrl) {
      return;
    }

    if (!apiUrl.startsWith('https://')) {
      return;
    }

    try {
      await fetch(`${apiUrl}/api/security-logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(entry),
        keepalive: true,
      });
    } catch {
      // Falha silenciosa para não impactar UX em produção.
    }
  }, [shouldSend]);

  return { sendSecurityLog };
}